import { useState } from 'react'
import Avatar from '../components/Avatar'
import {
  useComptes, useCreateCompte, useCreatePersonne, useDeleteCompte, useDeletePersonne,
  usePersonnes, useUpdateCompte, useUpdatePersonne,
} from '../api/hooks'
import type { Categorie, Personne as PersonneT } from '../api/types'

const CAT_SHORT: Record<Categorie, string> = {
  bebe: 'Bébé',
  enfant: 'Enfant',
  adolescent: 'Ado',
  adulte: 'Adulte',
}

export default function Familles() {
  const comptes = useComptes()
  const personnes = usePersonnes()
  const createCompte = useCreateCompte()
  const [nouvelleFamille, setNouvelleFamille] = useState('')

  return (
    <div className="space-y-4">
      <section className="card p-4 sm:p-5">
        <h2 className="text-lg sm:text-xl font-bold text-ocean-900 mb-1">
          👨‍👩‍👧 Familles & personnes
        </h2>
        <p className="text-xs sm:text-sm text-ocean-700 mb-4">
          Ajouter, modifier, supprimer des participants. Cliquer sur la photo
          pour ajouter ou remplacer un avatar.
        </p>

        {/* Légende catégories tarifaires (cf demande) */}
        <CategoriesLegend />

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!nouvelleFamille.trim()) return
            createCompte.mutate(
              { nom: nouvelleFamille.trim(), rang: (comptes.data?.length ?? 0) + 1 },
              { onSuccess: () => setNouvelleFamille('') },
            )
          }}
          className="flex flex-col sm:flex-row gap-2 sm:items-end mt-4"
        >
          <div className="flex-1">
            <label className="label">Nouvelle famille</label>
            <input
              type="text"
              value={nouvelleFamille}
              onChange={e => setNouvelleFamille(e.target.value)}
              placeholder="Ex. Caroline, Pierre (Bdx)…"
              className="input"
            />
          </div>
          <button type="submit" disabled={!nouvelleFamille.trim()} className="btn-coral">
            + Ajouter
          </button>
        </form>
      </section>

      <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {comptes.data?.map(c => (
          <FamilleCard
            key={c.id}
            compte={c}
            personnes={personnes.data?.filter(p => p.compte_id === c.id && p.actif) ?? []}
          />
        ))}
      </section>
    </div>
  )
}

function CategoriesLegend() {
  return (
    <div className="inline-block border border-ocean-200 rounded-lg overflow-hidden bg-white shadow-soft">
      <div className="bg-ocean-50 border-b border-ocean-100 px-4 py-1.5 text-center font-bold text-ocean-900 text-sm">
        Catégories Tarif
      </div>
      <div className="px-4 py-2 text-sm italic space-y-0.5 text-ocean-800 text-center">
        <div><strong className="not-italic">Bébé</strong> [0 - 2 ans] : 0 %</div>
        <div><strong className="not-italic">Enfant</strong> [3 - 12 ans] : 80 %</div>
        <div><strong className="not-italic">Adolescent</strong> [13 - 17 ans] : 100 %</div>
        <div><strong className="not-italic">Adulte</strong> [18 ans et +] : 100 %</div>
      </div>
    </div>
  )
}

function FamilleCard({
  compte, personnes,
}: { compte: { id: number; nom: string; rang: number }; personnes: PersonneT[] }) {
  const update = useUpdateCompte()
  const del = useDeleteCompte()
  const createPersonne = useCreatePersonne()
  const updatePersonne = useUpdatePersonne()
  const deletePersonne = useDeletePersonne()
  const [editing, setEditing] = useState(false)
  const [nom, setNom] = useState(compte.nom)
  const [newPrenom, setNewPrenom] = useState('')
  const [newCat, setNewCat] = useState<Categorie>('adulte')

  return (
    <div className="card p-3 sm:p-4 card-hover">
      <div className="flex items-center justify-between gap-2 mb-3">
        {editing ? (
          <input
            value={nom}
            onChange={e => setNom(e.target.value)}
            className="input py-1 text-sm"
            autoFocus
          />
        ) : (
          <h3 className="font-bold text-ocean-900 flex-1 truncate">{compte.nom}</h3>
        )}
        <div className="flex gap-1 shrink-0">
          {editing ? (
            <>
              <button
                onClick={() => {
                  update.mutate({ id: compte.id, nom: nom.trim(), rang: compte.rang })
                  setEditing(false)
                }}
                className="btn-primary btn-sm"
              >OK</button>
              <button onClick={() => { setNom(compte.nom); setEditing(false) }} className="btn-ghost btn-sm">Annuler</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="btn-icon" title="Renommer" aria-label="Renommer">✏️</button>
              <button
                onClick={() => {
                  if (confirm(`Supprimer la famille « ${compte.nom} » et ses ${personnes.length} membres ?`))
                    del.mutate(compte.id)
                }}
                className="btn-icon hover:bg-coral-50" title="Supprimer" aria-label="Supprimer"
              >🗑️</button>
            </>
          )}
        </div>
      </div>

      <ul className="space-y-1.5 mb-3 min-h-[2rem]">
        {personnes.map(p => (
          <li key={p.id} className="flex items-center gap-2 text-sm">
            <Avatar
              personne_id={p.id}
              prenom={p.prenom}
              avatar_version={p.avatar_version}
              size="md"
              editable
            />
            <span className="flex-1 min-w-0 truncate text-ocean-900 font-medium">{p.prenom}</span>
            <select
              value={p.categorie}
              onChange={e => updatePersonne.mutate({ id: p.id, categorie: e.target.value as Categorie })}
              className="text-xs border border-ocean-200 rounded px-1.5 py-0.5 bg-white shrink-0"
              aria-label={`Catégorie de ${p.prenom}`}
            >
              <option value="bebe">{CAT_SHORT.bebe}</option>
              <option value="enfant">{CAT_SHORT.enfant}</option>
              <option value="adolescent">{CAT_SHORT.adolescent}</option>
              <option value="adulte">{CAT_SHORT.adulte}</option>
            </select>
            <button
              onClick={() => deletePersonne.mutate({ id: p.id })}
              className="text-coral-400 hover:text-coral-600 text-lg leading-none px-1"
              title="Retirer"
              aria-label={`Retirer ${p.prenom}`}
            >×</button>
          </li>
        ))}
        {personnes.length === 0 && (
          <li className="text-xs text-ocean-500 italic py-1">Aucune personne</li>
        )}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!newPrenom.trim()) return
          createPersonne.mutate(
            { compte_id: compte.id, prenom: newPrenom.trim(), categorie: newCat, ordre: personnes.length },
            { onSuccess: () => setNewPrenom('') },
          )
        }}
        className="flex gap-1 pt-2 border-t border-ocean-50"
      >
        <input
          value={newPrenom}
          onChange={e => setNewPrenom(e.target.value)}
          placeholder="Prénom"
          className="flex-1 min-w-0 text-sm input py-1"
        />
        <select
          value={newCat}
          onChange={e => setNewCat(e.target.value as Categorie)}
          className="text-sm border border-ocean-200 rounded px-1.5 bg-white"
          aria-label="Catégorie"
        >
          <option value="adulte">{CAT_SHORT.adulte}</option>
          <option value="adolescent">{CAT_SHORT.adolescent}</option>
          <option value="enfant">{CAT_SHORT.enfant}</option>
          <option value="bebe">{CAT_SHORT.bebe}</option>
        </select>
        <button type="submit" disabled={!newPrenom.trim()} className="btn-primary btn-sm">+</button>
      </form>
    </div>
  )
}
